interface SecurityHeaders {
  authorization: string;
  clientType: string;
  timestampSlice: string;
  secret: string;
}

const encoder = new TextEncoder();

/**
 * Cache cho WebCrypto key + secret — tái sử dụng trong cùng một phút.
 */
interface CryptoCache {
  timestampSlice: string;
  baseSecret: string;
  cryptoKey: CryptoKey;
  secret: string;
}

let _cryptoCache: CryptoCache | null = null;

async function getOrCreateCryptoCache(clientType: string, secretConfig: string): Promise<CryptoCache> {
  const timestampSlice = String(Math.floor(Date.now() / 60000));
  const baseSecret = `${clientType}${secretConfig}`;

  if (_cryptoCache && _cryptoCache.timestampSlice === timestampSlice && _cryptoCache.baseSecret === baseSecret) {
    return _cryptoCache;
  }

  const keyBytes = toBytes(baseSecret);
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    toArrayBuffer(keyBytes),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const secretBytes = await hmacSha256Keyed(cryptoKey, toBytes(timestampSlice));
  const secret = toBase64(secretBytes);

  _cryptoCache = { timestampSlice, baseSecret, cryptoKey, secret };
  return _cryptoCache;
}

/**
 * Tạo security headers cho mỗi request.
 * WebCrypto key được cache theo phút — tránh importKey mỗi request.
 */
export async function generateRequestHeaders(
  clientType: string,
  secretConfig: string,
  authorization?: string | null,
): Promise<SecurityHeaders> {
  const cache = await getOrCreateCryptoCache(clientType, secretConfig);
  const { timestampSlice, secret, cryptoKey } = cache;

  if (authorization) {
    return { authorization, clientType, timestampSlice, secret };
  }

  const nowSec = Math.floor(Date.now() / 1000);
  const header = toBase64Url(toBytes(JSON.stringify({ alg: 'HS256', typ: 'JWT' })));
  const payload = toBase64Url(
    toBytes(
      JSON.stringify({
        timestamp: nowSec,
        iat: nowSec,
        exp: nowSec + 30 * 60,
      }),
    ),
  );

  const signingInput = `${header}.${payload}`;
  const signature = await hmacSha256Keyed(cryptoKey, toBytes(signingInput));

  return {
    authorization: `Bearer ${signingInput}.${toBase64Url(signature)}`,
    clientType,
    timestampSlice,
    secret,
  };
}

function toBytes(value: string): Uint8Array {
  return encoder.encode(value);
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

/** Ký bằng CryptoKey đã có sẵn (không importKey lại). */
async function hmacSha256Keyed(cryptoKey: CryptoKey, message: Uint8Array): Promise<Uint8Array> {
  const signature = await crypto.subtle.sign('HMAC', cryptoKey, toArrayBuffer(message));
  return new Uint8Array(signature);
}

function toBase64(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes));
}

function toBase64Url(bytes: Uint8Array): string {
  return toBase64(bytes).replace(/=+$/g, '').replace(/\+/g, '-').replace(/\//g, '_');
}
